import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Phone } from 'lucide-react';
import { services } from '../data';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const { ref: headerRef, isInView: headerInView } = useScrollAnimation({ threshold: 0.1 });
  
  const serviceId = parseInt(id || '1');
  const service = services.find(s => s.id === serviceId) || services[0];
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section 
        ref={headerRef}
        className="relative pt-32 pb-16 overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-950" />
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
          className="absolute top-20 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-primary-600/20 to-primary-800/10 rounded-full blur-3xl"
        />
        
        <div className="relative max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <Link 
              to="/produk-layanan"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Kembali ke Produk & Layanan</span>
            </Link>
            
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl font-display font-bold text-white/10">0{service.id}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              {service.title}
            </h1>
            
            <p className="text-xl text-white/70 mb-8 leading-relaxed">
              {service.description}
            </p>
            
            <motion.a
              href="tel:+6281234567890"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Content Placeholder */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto section-padding">
          <div className="glass rounded-2xl p-8 md:p-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <p className="text-lg text-white/70 mb-4">
                Detail lengkap untuk layanan <strong className="text-white">{service.title}</strong> akan segera ditambahkan.
              </p>
              
              <p className="text-white/60">
                Untuk informasi lebih lanjut, silakan hubungi kami langsung.
              </p>
            </motion.div>

            {/* Navigation to other products */}
            <div className="border-t border-white/10 pt-8">
              <h3 className="text-lg font-semibold text-white mb-4">Layanan Lainnya</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services
                  .filter(s => s.id !== serviceId)
                  .slice(0, 3)
                  .map((otherService) => (
                    <Link
                      key={otherService.id}
                      to={`/produk-layanan/${otherService.id}`}
                      className="group p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white/80 group-hover:text-white text-sm font-medium">
                          {otherService.title}
                        </span>
                        <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
